import { signInWithCustomToken } from "firebase/auth";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useRef, useState } from "react";
import { auth } from "../../firebaseConfig";
import { endpoints } from "../../hooks/endpoints";
import { useAppDispatch } from "../../store/hooks";
import { userData } from "../../store/slices/storeSlice";
import { Button } from "../ui/button";
import { TutorFormDataI } from "./TutorRegistration";
import { tutorSubjects } from "./TutorRegistrationStepOne";

const AssignTutorPassword = ({
  formData,
  handleNext,
  handlePrev,
  setFormData,
}: {
  formData: TutorFormDataI;
  handleNext: () => void;
  handlePrev: () => void;
  setFormData: React.Dispatch<React.SetStateAction<TutorFormDataI>>;
}) => {
  const passwordRef = useRef<any>(null);
  const passwordRef2 = useRef<any>(null);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    if (passwordRef.current.value !== passwordRef2.current.value) {
      return setError("كلمة المرور غير متطابقة");
    }

    try {
      setIsPending(true);
      setError("");

      const response = await fetch(endpoints.createTutor, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          password: passwordRef.current.value,
          subject_name:
            formData.subject === "other"
              ? formData.other_subject
              : tutorSubjects.find((sub) => sub.key === formData.subject)?.name,
        }),
      });

      const res = await response.json();

      if (!response.ok) {
        throw Error(res.data);
      }

      setFormData((prev) => ({
        ...prev,
        password: passwordRef.current.value,
        subject_name:
          formData.subject === "other"
            ? formData.other_subject
            : tutorSubjects.find((sub) => sub.key === formData.subject)?.name,
      }));

      if (res.data.token) {
        await signInWithCustomToken(auth, res.data.firebase_token)
          .then((userCredential) => {
            const user = userCredential.user;
            console.log({ user });
            dispatch(userData(res.data));
            localStorage.setItem("user", JSON.stringify(res.data));
            handleNext();
          })
          .catch((error) => {
            const errorCode = error.code;
            const errorMessage = error.message;
            throw new Error(errorMessage);
          });
      }
    } catch (e) {
      console.error(e.message);
      setError(e.message || "حدث خطأ يرجى المحاولة مرة أخرى");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmitPassword} className="space-y-4">
      <h4 className="mx-auto mb-4 pb-1 text-xl font-semibold border-b-2 border-(--primary-color-light) w-fit">
        يرجى تعيين كلمة المرور
      </h4>

      {/* Password Field */}
      <div className="w-full mx-auto">
        <label className="block font-bold mb-2">كلمة المرور</label>
        <div className="flex gap-3 items-center relative">
          <input
            style={{ direction: "rtl", textAlign: "right" }}
            ref={passwordRef}
            type={showPassword ? "text" : "password"}
            name="password1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-end"
            disabled={isPending}
          />
          <Button
            variant="link"
            className="p-0 absolute left-2 top-1/2 -translate-y-1/2"
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? (
              <EyeOffIcon className="size-6" />
            ) : (
              <EyeIcon className="size-6" />
            )}
          </Button>
        </div>
      </div>

      {/* Confirm Password Field */}
      <div className="w-full mx-auto">
        <label className="block font-bold mb-2">اكتب كلمة المرور مجدداً</label>
        <div className="flex gap-3 items-center relative">
          <input
            style={{ direction: "rtl", textAlign: "right" }}
            ref={passwordRef2}
            type={showPassword ? "text" : "password"}
            name="password2"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-end"
            disabled={isPending}
          />
          <Button
            variant="link"
            className="p-0 absolute left-2 top-1/2 -translate-y-1/2"
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            {showPassword ? (
              <EyeOffIcon className="size-6" />
            ) : (
              <EyeIcon className="size-6" />
            )}
          </Button>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-center gap-3 mt-4">
        <button
          type="button"
          onClick={handlePrev}
          disabled={isPending}
          className="px-4 py-2 rounded-md bg-gray-300 text-gray-800 hover:bg-gray-400 transition disabled:opacity-50"
        >
          رجوع
        </button>

        <button
          type="submit"
          disabled={isPending}
          className="px-4 py-2 rounded-md bg-primary text-white hover:bg-(--primary-color-dark) transition disabled:opacity-50"
        >
          {isPending ? "التالي ..." : "التالي"}
        </button>
      </div>

      {/* Error Alert */}
      {!!error && (
        <div className="mt-4 w-full text-center bg-red-100 text-red-700 py-2 px-4 rounded-md">
          {error}
        </div>
      )}
    </form>
  );
};

export default AssignTutorPassword;
